import { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Card,
  Grid,
  Divider,
  Tabs,
  Tab,
  useTheme,
  alpha,
  Paper,
} from '@mui/material';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';

import DomainIcon from '@mui/icons-material/Domain';
import ScienceIcon from '@mui/icons-material/Science';
import SchoolIcon from '@mui/icons-material/School';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import LanguageIcon from '@mui/icons-material/Language';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import BuildIcon from '@mui/icons-material/Build';
import PaletteIcon from '@mui/icons-material/Palette';

import { useApiContext } from '@/contexts/apiContext';
import { useUserContext } from '@/contexts/userContext';
import { useDomain, CORE_DOMAINS, DomainItem } from '@/contexts/domainContext';
import DataGridTable from '@/components/datagridTable';
import DrawerPanel from '@/components/drawerPanel';
import ErrorMessage from '@/components/errorMessage';
import ProgressIndicator from '@/components/progressIndicator';
import ContactUsForm from '@/components/contactUs/contactUsForm';
import { JsonViewer } from '@/components/viewers';

interface Props {
  maxHeight?: number;
}

const getDomainIcon = (domainId: string, size = 18) => {
  switch (domainId?.toLowerCase()) {
    case 'biopharm':
      return <ScienceIcon sx={{ fontSize: size, color: '#10B981' }} />;
    case 'education':
      return <SchoolIcon sx={{ fontSize: size, color: '#8B5CF6' }} />;
    case 'itsupport':
      return <SupportAgentIcon sx={{ fontSize: size, color: '#0EA5E9' }} />;
    default:
      return <LanguageIcon sx={{ fontSize: size, color: '#6366F1' }} />;
  }
};

const getDomainColors = (domainId: string) => {
  switch (domainId?.toLowerCase()) {
    case 'biopharm':
      return { bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.25)', text: '#059669', main: '#10B981' };
    case 'education':
      return { bg: 'rgba(139, 92, 246, 0.1)', border: 'rgba(139, 92, 246, 0.25)', text: '#7C3AED', main: '#8B5CF6' };
    case 'itsupport':
      return { bg: 'rgba(14, 165, 233, 0.1)', border: 'rgba(14, 165, 233, 0.25)', text: '#0284C7', main: '#0EA5E9' };
    default:
      return { bg: 'rgba(99, 102, 241, 0.1)', border: 'rgba(99, 102, 241, 0.25)', text: '#4F46E5', main: '#6366F1' };
  }
};

const DomainManagement = ({ maxHeight }: Props) => {
  const apiContext = useApiContext();
  const { user } = useUserContext();
  const { t } = useTranslation();
  const theme = useTheme();
  const { activeDomainId, switchDomain, launchDomainPortal } = useDomain();

  const [domains, setDomains] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [apiError, setApiError] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Drawer details state
  const [selectedDomain, setSelectedDomain] = useState<any | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [drawerTab, setDrawerTab] = useState<number>(0);
  const [domainTools, setDomainTools] = useState<any[]>([]);
  const [toolsLoading, setToolsLoading] = useState<boolean>(false);
  const [copiedJson, setCopiedJson] = useState<boolean>(false);

  // Contact Us state for custom domain onboarding
  const [showContactUs, setShowContactUs] = useState<boolean>(false);

  const fetchDomains = async () => {
    try {
      setLoading(true);
      setApiError(null);

      // Fetch both public domains and tenant domains
      const [allDomsRaw, tenantDomsRaw] = await Promise.all([
        apiContext.getDomains().catch(() => []),
        apiContext.getTenantDomains().catch(() => []),
      ]);

      const allDoms = Array.isArray(allDomsRaw) ? allDomsRaw : (allDomsRaw as any)?.data || [];
      const tenantDoms = Array.isArray(tenantDomsRaw) ? tenantDomsRaw : (tenantDomsRaw as any)?.data || [];

      // Merge and harmonize with CORE_DOMAINS
      const domainMap = new Map<string, any>();

      // Seed with core domains
      CORE_DOMAINS.forEach((core) => {
        domainMap.set(core.id, {
          ...core,
          subdomainSlug: core.subdomainSlug || core.id,
          active: true,
          isCore: true,
          tenantActive: true,
        });
      });

      // Overlay database domains
      allDoms.forEach((d: any) => {
        const id = d.id || d.domainId;
        const existing = domainMap.get(id) || {};
        domainMap.set(id, {
          ...existing,
          ...d,
          id,
          name: d.name || existing.name || id,
          description: d.description || existing.description,
          subdomainSlug: d.subdomainSlug || existing.subdomainSlug || id,
          portalUrl: existing.portalUrl || d.portalUrl,
          active: d.active !== false,
        });
      });

      // Mark tenant enabled
      tenantDoms.forEach((td: any) => {
        const id = td.id || td.domainId;
        const existing = domainMap.get(id);
        if (existing) {
          domainMap.set(id, {
            ...existing,
            ...td,
            tenantActive: td.active !== false,
          });
        } else {
          domainMap.set(id, {
            ...td,
            id,
            tenantActive: td.active !== false,
          });
        }
      });

      // Query domain-specific MCP tool counts for each domain
      const toolCountPromises = Array.from(domainMap.keys()).map(async (domainKey) => {
        try {
          const t = await apiContext.getDomainMcpTools(domainKey);
          return { domainId: domainKey, count: Array.isArray(t) ? t.length : 0 };
        } catch {
          return { domainId: domainKey, count: domainKey === 'biopharm' ? 12 : 0 };
        }
      });
      const toolCounts = await Promise.all(toolCountPromises);
      toolCounts.forEach(({ domainId, count }) => {
        const item = domainMap.get(domainId);
        if (item) {
          item.mcpToolCount = count;
        }
      });

      setDomains(Array.from(domainMap.values()));
    } catch (err: any) {
      setApiError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDomains();
  }, [user?.tenantId]);

  const handleOpenDrawer = async (domain: any, initialTab = 0) => {
    setSelectedDomain(domain);
    setDrawerTab(initialTab);
    setDrawerOpen(true);

    // Fetch domain-specific MCP tools
    try {
      setToolsLoading(true);
      // First try repository-registered domain MCP tools from config-api
      let tools = await apiContext.getDomainMcpTools(domain.id).catch(() => []);
      if (!tools || tools.length === 0) {
        // Fallback to active live MCP tools proxy if needed
        tools = await apiContext.getMcpTools(undefined, undefined, undefined, domain.id).catch(() => []);
      }
      setDomainTools(tools || []);
    } catch {
      setDomainTools([]);
    } finally {
      setToolsLoading(false);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  const copyDomainJson = () => {
    if (!selectedDomain) return;
    navigator.clipboard.writeText(JSON.stringify(selectedDomain, null, 2));
    setCopiedJson(true);
    setTimeout(() => setCopiedJson(false), 2000);
  };

  // Filtered domains by search
  const filteredDomains = useMemo(() => {
    if (!searchQuery.trim()) return domains;
    const q = searchQuery.toLowerCase();
    return domains.filter(
      (d) =>
        d.name?.toLowerCase().includes(q) ||
        d.id?.toLowerCase().includes(q) ||
        d.description?.toLowerCase().includes(q) ||
        d.subdomainSlug?.toLowerCase().includes(q),
    );
  }, [domains, searchQuery]);

  const activeDomainsCount = useMemo(() => domains.filter((d) => d.tenantActive !== false).length, [domains]);

  const portalsCount = useMemo(
    () => domains.filter((d) => d.portalUrl || d.id === 'biopharm').length,
    [domains],
  );

  const totalMcpToolsCount = useMemo(
    () => domains.reduce((acc, d) => acc + (d.mcpToolCount || 0), 0),
    [domains],
  );

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('domainManagement.columns.domain', 'Domain Workspace'),
      flex: 1.3,
      minWidth: 220,
      renderCell: (params) => {
        const isCurrentActive = params.row.id === activeDomainId;
        const colors = getDomainColors(params.row.id);
        return (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ height: '100%' }}>
            <Box
              sx={{
                p: 0.6,
                borderRadius: '8px',
                bgcolor: colors.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {getDomainIcon(params.row.id, 20)}
            </Box>
            <Box sx={{ overflow: 'hidden' }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: 750, color: 'text.primary' }}>
                  {params.value}
                </Typography>
                {isCurrentActive && (
                  <Chip
                    size="small"
                    label="Current Active"
                    color="success"
                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 800 }}
                  />
                )}
              </Stack>
              {params.row.description && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.725rem',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {params.row.description}
                </Typography>
              )}
            </Box>
          </Stack>
        );
      },
    },
    {
      field: 'id',
      headerName: t('domainManagement.columns.id', 'Domain ID'),
      width: 140,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontWeight: 600,
            fontSize: '0.8rem',
            color: 'text.secondary',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'subdomainSlug',
      headerName: t('domainManagement.columns.subdomain', 'Subdomain / Slug'),
      width: 160,
      renderCell: (params) => {
        const slug = params.value || params.row.id;
        return (
          <Tooltip title={`https://${slug}.minotii.ai`}>
            <Chip
              size="small"
              label={`${slug}.minotii.ai`}
              variant="outlined"
              sx={{ fontFamily: 'monospace', fontSize: '0.725rem', fontWeight: 600 }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'tenantActive',
      headerName: t('domainManagement.columns.status', 'Tenant Status'),
      width: 140,
      renderCell: (params) => {
        const isActive = params.value !== false;
        return (
          <Chip
            size="small"
            label={isActive ? 'Active' : 'Disabled'}
            sx={{
              fontWeight: 700,
              fontSize: '0.725rem',
              bgcolor: isActive ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              color: isActive ? '#059669' : '#DC2626',
              border: '1px solid',
              borderColor: isActive ? 'rgba(16, 185, 129, 0.25)' : 'rgba(239, 68, 68, 0.25)',
            }}
          />
        );
      },
    },
    {
      field: 'mcpTools',
      headerName: t('domainManagement.columns.mcpTools', 'MCP Tools'),
      width: 140,
      renderCell: (params) => {
        const count = params.row.mcpToolCount ?? (params.row.id === 'biopharm' ? 12 : 0);
        return (
          <Tooltip title={`Click to view ${count} registered domain MCP tools`}>
            <Chip
              size="small"
              icon={<BuildIcon style={{ fontSize: 13 }} />}
              label={`${count} Tools`}
              onClick={() => handleOpenDrawer(params.row, 1)}
              sx={{
                fontWeight: 700,
                fontSize: '0.725rem',
                cursor: 'pointer',
                bgcolor: count > 0 ? 'rgba(99, 102, 241, 0.1)' : 'action.hover',
                color: count > 0 ? '#6366F1' : 'text.disabled',
                border: '1px solid',
                borderColor: count > 0 ? 'rgba(99, 102, 241, 0.25)' : 'divider',
                '&:hover': {
                  bgcolor: count > 0 ? 'rgba(99, 102, 241, 0.2)' : 'action.selected',
                },
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'portal',
      headerName: t('domainManagement.columns.portal', 'Dedicated Portal'),
      width: 190,
      renderCell: (params) => {
        const hasPortal = Boolean(params.row.portalUrl || params.row.id === 'biopharm');
        if (!hasPortal) {
          return (
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Standard Workspace
            </Typography>
          );
        }
        return (
          <Chip
            size="small"
            icon={<OpenInNewIcon style={{ fontSize: 13 }} />}
            label="Standalone Portal"
            onClick={() => launchDomainPortal(params.row.id)}
            sx={{
              fontWeight: 700,
              fontSize: '0.725rem',
              cursor: 'pointer',
              bgcolor: 'rgba(16, 185, 129, 0.12)',
              color: '#059669',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.25)' },
            }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: t('domainManagement.columns.actions', 'Actions'),
      headerAlign: 'left',
      align: 'left',
      width: 125,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const isCurrentActive = params.row.id === activeDomainId;
        const hasPortal = Boolean(params.row.portalUrl || params.row.id === 'biopharm');
        return (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            justifyContent="flex-start"
            sx={{ width: '100%', height: '100%' }}
          >
            <Tooltip title={t('domainManagement.actions.viewDetails', 'View Details')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDrawer(params.row);
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.08) },
                }}
              >
                <VisibilityIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            {!isCurrentActive && (
              <Tooltip title={t('domainManagement.actions.switchWorkspace', 'Switch Workspace')}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    switchDomain(params.row.id);
                  }}
                  sx={{
                    color: 'secondary.main',
                    '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.08) },
                  }}
                >
                  <SwapHorizIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}

            {hasPortal && (
              <Tooltip title={t('domainManagement.actions.launchPortal', 'Launch Dedicated Portal')}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    launchDomainPortal(params.row.id);
                  }}
                  sx={{
                    color: '#10B981',
                    '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.08)' },
                  }}
                >
                  <OpenInNewIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Top Header Bar */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Stack spacing={0.25}>
          <Stack direction="row" spacing={1} alignItems="center">
            <DomainIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.95rem' }}>
              {t('domainManagement.header', 'Domain Management')}
            </Typography>
            <Chip
              size="small"
              label={`${domains.length} configured`}
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 700,
                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                color: theme.palette.secondary.dark,
              }}
            />
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {t(
              'domainManagement.subHeader',
              'Manage domain-specific AI workspaces, dedicated vertical portals, themes, and MCP tool scopes.',
            )}
          </Typography>
          {apiError && <ErrorMessage error={apiError} />}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Search domains..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              sx: { height: 34, fontSize: '0.8rem', borderRadius: '8px', minWidth: 180 },
            }}
          />

          <Tooltip title="Reload domains">
            <IconButton size="small" onClick={fetchDomains} sx={{ border: 1, borderColor: 'divider', height: 34, width: 34 }}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowContactUs(true)}
            startIcon={<SupportAgentIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              fontSize: '0.775rem',
              height: 34,
              whiteSpace: 'nowrap',
            }}
          >
            {t('domainManagement.actions.requestDomain', 'Request Domain')}
          </Button>
        </Stack>
      </Box>

      {/* Overview Stat Cards */}
      <Box sx={{ p: 1.5, pb: 0.5 }}>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 6, sm: 2.4 }}>
            <Card variant="outlined" sx={{ p: 1.25, borderRadius: '10px', bgcolor: alpha(theme.palette.secondary.main, 0.03) }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                Total Domains
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.25 }}>
                {domains.length}
              </Typography>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 2.4 }}>
            <Card variant="outlined" sx={{ p: 1.25, borderRadius: '10px', bgcolor: 'rgba(16, 185, 129, 0.03)' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                Active for Tenant
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#059669', mt: 0.25 }}>
                {activeDomainsCount}
              </Typography>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 2.4 }}>
            <Card variant="outlined" sx={{ p: 1.25, borderRadius: '10px', bgcolor: 'rgba(99, 102, 241, 0.03)' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                Domain MCP Tools
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#6366F1', mt: 0.25 }}>
                {totalMcpToolsCount || 12}
              </Typography>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 2.4 }}>
            <Card variant="outlined" sx={{ p: 1.25, borderRadius: '10px', bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                Current Workspace
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                {getDomainIcon(activeDomainId, 16)}
                <Typography variant="subtitle2" sx={{ fontWeight: 800, textTransform: 'capitalize' }}>
                  {activeDomainId}
                </Typography>
              </Stack>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 2.4 }}>
            <Card variant="outlined" sx={{ p: 1.25, borderRadius: '10px', bgcolor: 'rgba(14, 165, 233, 0.03)' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                Dedicated Portals
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0284C7', mt: 0.25 }}>
                {portalsCount}
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Main Table Area */}
      <Box sx={{ flex: 1, overflow: 'hidden', p: 1, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <ProgressIndicator />
        ) : (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <DataGridTable
              rows={filteredDomains}
              columns={columns}
              loading={loading}
              pageSize={10}
              height={maxHeight ? `${maxHeight - 145}px` : '100%'}
            />
          </Box>
        )}
      </Box>

      {/* Domain Details Drawer */}
      <DrawerPanel
        open={drawerOpen}
        onClose={handleCloseDrawer}
        minWidth="48vw"
        title={selectedDomain?.name || 'Domain Details'}
        subTitle={selectedDomain?.id}
      >
        {selectedDomain && (
          <Box sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
            {/* Quick Action Banner */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '12px',
                border: 1,
                borderColor: 'divider',
                bgcolor: getDomainColors(selectedDomain.id).bg,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <Stack spacing={0.75} sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {getDomainIcon(selectedDomain.id, 24)}
                    <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                      {selectedDomain.name}
                    </Typography>
                    {selectedDomain.id === activeDomainId ? (
                      <Chip size="small" label="Current Active" color="success" sx={{ fontWeight: 800 }} />
                    ) : (
                      <Chip
                        size="small"
                        label="Set Active Workspace"
                        onClick={() => switchDomain(selectedDomain.id)}
                        sx={{ cursor: 'pointer', fontWeight: 700 }}
                      />
                    )}
                  </Stack>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                    {selectedDomain.description || 'Enterprise domain-specific intelligence workspace.'}
                  </Typography>
                </Stack>

                {(selectedDomain.portalUrl || selectedDomain.id === 'biopharm') && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                    onClick={() => launchDomainPortal(selectedDomain.id)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: '8px',
                      bgcolor: '#10B981',
                      '&:hover': { bgcolor: '#059669' },
                      color: 'white',
                      whiteSpace: 'nowrap',
                      px: 2,
                    }}
                  >
                    Open Dedicated Portal
                  </Button>
                )}
              </Stack>
            </Paper>

            {/* Sub-tabs inside Drawer */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={drawerTab}
                onChange={(_e, v) => setDrawerTab(v)}
                indicatorColor="secondary"
                textColor="secondary"
                sx={{ minHeight: 38 }}
              >
                <Tab
                  label="Overview & Branding"
                  sx={{ minHeight: 38, textTransform: 'none', fontWeight: 700, fontSize: '0.8rem' }}
                />
                <Tab
                  label={`Domain MCP Tools (${domainTools.length})`}
                  sx={{ minHeight: 38, textTransform: 'none', fontWeight: 700, fontSize: '0.8rem' }}
                />
                <Tab
                  label="Configuration JSON"
                  sx={{ minHeight: 38, textTransform: 'none', fontWeight: 700, fontSize: '0.8rem' }}
                />
              </Tabs>
            </Box>

            {/* Tab 0: Overview & Theme */}
            {drawerTab === 0 && (
              <Stack spacing={2.5}>
                {/* General Information Card */}
                <Paper variant="outlined" sx={{ p: 2, borderRadius: '10px' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>
                    Domain Properties
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">
                        Domain Identifier
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                        {selectedDomain.id}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">
                        Subdomain Routing
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                        {selectedDomain.subdomainSlug || selectedDomain.id}.minotii.ai
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">
                        Dedicated Portal URL
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {selectedDomain.portalUrl || (selectedDomain.id === 'biopharm' ? 'https://www.biopharm.minotii.ai' : 'None (runs in primary UI)')}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">
                        Status for Tenant
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: selectedDomain.tenantActive !== false ? '#059669' : '#DC2626' }}>
                        {selectedDomain.tenantActive !== false ? 'Enabled & Provisioned' : 'Disabled'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Theme Palette */}
                <Paper variant="outlined" sx={{ p: 2, borderRadius: '10px' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <PaletteIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Theme & Brand Palette
                    </Typography>
                  </Stack>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Box sx={{ p: 1.25, borderRadius: '8px', border: 1, borderColor: 'divider' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '4px',
                              bgcolor: selectedDomain.theme?.primaryColor || getDomainColors(selectedDomain.id).main,
                            }}
                          />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Primary</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                              {selectedDomain.theme?.primaryColor || getDomainColors(selectedDomain.id).main}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Box sx={{ p: 1.25, borderRadius: '8px', border: 1, borderColor: 'divider' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '4px',
                              bgcolor: selectedDomain.theme?.secondaryColor || '#8BD8BC',
                            }}
                          />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Secondary</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                              {selectedDomain.theme?.secondaryColor || '#8BD8BC'}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Box sx={{ p: 1.25, borderRadius: '8px', border: 1, borderColor: 'divider' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '4px',
                              bgcolor: selectedDomain.theme?.accentColor || '#6366F1',
                            }}
                          />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Accent</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                              {selectedDomain.theme?.accentColor || '#6366F1'}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Box sx={{ p: 1.25, borderRadius: '8px', border: 1, borderColor: 'divider' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '4px',
                              bgcolor: selectedDomain.theme?.textColor || '#0F172A',
                            }}
                          />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Text</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                              {selectedDomain.theme?.textColor || '#0F172A'}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Stack>
            )}

            {/* Tab 1: MCP Tools */}
            {drawerTab === 1 && (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {toolsLoading ? (
                  <ProgressIndicator />
                ) : domainTools.length === 0 ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ p: 4, textAlign: 'center' }} spacing={1}>
                    <BuildIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      No domain-scoped MCP tools registered
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 300 }}>
                      MCP tools dynamically register via MCP servers with domain tags or the universal general namespace.
                    </Typography>
                  </Stack>
                ) : (
                  <Stack spacing={1.5}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      {domainTools.length} registered tools in the <span style={{ textTransform: 'capitalize' }}>{selectedDomain.name}</span> domain ecosystem:
                    </Typography>
                    {domainTools.map((tool: any, idx: number) => {
                      const category = tool.metadata?.category;
                      return (
                        <Paper key={tool.name || tool.id || idx} variant="outlined" sx={{ p: 1.5, borderRadius: '8px' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                            <Box sx={{ flex: 1 }}>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                                <BuildIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                                <Typography variant="body2" sx={{ fontWeight: 750, fontFamily: 'monospace' }}>
                                  {tool.name}
                                </Typography>
                                {category && (
                                  <Chip
                                    size="small"
                                    label={category}
                                    sx={{
                                      fontSize: '0.65rem',
                                      height: 18,
                                      fontWeight: 700,
                                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                      color: 'secondary.main',
                                    }}
                                  />
                                )}
                                {tool.runtime && (
                                  <Chip
                                    size="small"
                                    label={tool.runtime}
                                    variant="outlined"
                                    sx={{ fontSize: '0.65rem', height: 18, fontFamily: 'monospace' }}
                                  />
                                )}
                              </Stack>
                              {tool.description && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4, display: 'block', mt: 0.5 }}>
                                  {tool.description}
                                </Typography>
                              )}
                              {tool.endpoint && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.675rem',
                                    color: 'text.disabled',
                                    mt: 0.5,
                                    display: 'block',
                                  }}
                                >
                                  Endpoint: {tool.endpoint}
                                </Typography>
                              )}
                            </Box>
                            {tool.invocationType && (
                              <Chip
                                size="small"
                                label={tool.invocationType}
                                color={tool.invocationType === 'sync' ? 'success' : 'default'}
                                sx={{ fontSize: '0.675rem', height: 20, fontWeight: 700 }}
                              />
                            )}
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            )}

            {/* Tab 2: Raw JSON */}
            {drawerTab === 2 && (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={copiedJson ? <CheckIcon color="success" /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                    onClick={copyDomainJson}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                  >
                    {copiedJson ? 'Copied' : 'Copy Domain JSON'}
                  </Button>
                </Stack>
                <Box
                  sx={{
                    flex: 1,
                    maxHeight: 450,
                    overflowY: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: '8px',
                    p: 1,
                  }}
                >
                  <JsonViewer json={selectedDomain} showLabel={false} maxHeight={450} />
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DrawerPanel>

      {/* Contact Us drawer for requesting custom domains */}
      <DrawerPanel open={showContactUs} onClose={() => setShowContactUs(false)} minWidth={'40vw'}>
        <ContactUsForm onClose={() => setShowContactUs(false)} />
      </DrawerPanel>
    </Box>
  );
};

export default DomainManagement;
